import React, { useState } from 'react';
import '../../styles/features/advisor-modal.css';
import {
  PositionAnalysisResponse,
  AIZone,
  AITrafficLight,
  AIActionIndicator,
  AIConfidence,
  AICampaignAnalysis,
  AILegInsight,
  AICampaignHealth,
  AILegEffectiveness,
  AIPremiumCaptureTrend,
  AIStrikeProgression,
  AIMarketContext,
  AIRollScenario,
  AITradeRecommendation
} from '../../types';

interface AIAdvisorModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticker: string;
  isLoading: boolean;
  error: string | null;
  recommendation: PositionAnalysisResponse | null;
  onRetry: () => void;
}

// Trader theme: tones are CSS classes (advisor-modal.css), not hex colours.
// Amber = warning, gain/loss = signed P/L only, everything else neutral.

// Zone label (emoji icons dropped in the trader theme — the zone name carries it)
const zoneLabel = (zone: AIZone): string => zone;

const getTrafficLightTone = (light: AITrafficLight): string => {
  switch (light) {
    case 'RED':
    case 'YELLOW':
    case 'GREEN':
      return `is-${light}`;
    default:
      return '';
  }
};

const getActionTone = (action: AIActionIndicator): string => {
  if (action.includes('EXIT')) return 'is-warn';
  if (action.includes('ROLL')) return 'is-warn';
  if (action.includes('CLOSE')) return '';
  if (action.includes('MONITOR')) return 'is-warn';
  if (action.includes('HOLD (RISK)')) return 'is-warn';
  return ''; // HOLD (BREAKOUT) / ON TRACK
};

const getConfidenceTone = (conf: AIConfidence): string => (conf === 'Low' ? 'is-warn' : '');

// Campaign Analysis Helper Functions
const getCampaignHealthTone = (health: AICampaignHealth): string => {
  switch (health) {
    case 'At Risk':
    case 'Recovering':
      return 'is-warn';
    default:
      return '';
  }
};

const getCampaignHealthIcon = (health: AICampaignHealth): string => {
  switch (health) {
    case 'Healthy':
      return '✓';
    case 'At Risk':
      return '!';
    case 'Recovering':
      return '↗';
    default:
      return '•';
  }
};

const getEffectivenessTone = (effectiveness: AILegEffectiveness): string =>
  effectiveness === 'Suboptimal' ? 'is-warn' : '';

const getTrendIcon = (trend: AIPremiumCaptureTrend): string => {
  switch (trend) {
    case 'Improving':
      return '↑';
    case 'Declining':
      return '↓';
    case 'Stable':
      return '→';
    default:
      return '•';
  }
};

const getProgressionIcon = (progression: AIStrikeProgression): string => {
  switch (progression) {
    case 'Rolling Up':
      return '↗';
    case 'Rolling Down':
      return '↘';
    case 'Sideways':
      return '→';
    default:
      return '•';
  }
};

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg className={`aam-chev${open ? ' is-open' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 4.5 6 7.5 9 4.5" />
  </svg>
);

const AIAdvisorModal: React.FC<AIAdvisorModalProps> = ({
  isOpen,
  onClose,
  ticker,
  isLoading,
  error,
  recommendation,
  onRetry
}) => {
  const [showLegHistory, setShowLegHistory] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  if (!isOpen) return null;

  return (
    <div
      className="aam-root"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="aam" role="dialog" aria-modal="true" aria-label={`AI Position Analysis ${ticker}`}>
        {/* Header */}
        <div className="aam-head">
          <div className="aam-id">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              <path d="M20 3v4" />
              <path d="M22 5h-4" />
              <path d="M4 17v2" />
              <path d="M5 18H3" />
            </svg>
            <span className="aam-title">AI Position Analysis</span>
            <span className="aam-ticker">{ticker}</span>
          </div>
          <button onClick={onClose} className="aam-x" aria-label="Close">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="aam-body">
          {/* Loading State */}
          {isLoading && (
            <div className="aam-state" role="status">
              <span className="aam-dots" aria-hidden="true"><i /><i /><i /></span>
              <span>Analyzing position...</span>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="aam-err" role="alert">
              <p className="aam-err-t">Analysis Error</p>
              <p className="aam-err-m">{error}</p>
              <button onClick={onRetry} className="aam-btn is-ghost">
                Try Again
              </button>
            </div>
          )}

          {/* Success State */}
          {recommendation && !isLoading && !error && (
            <div>
              {/* Zone with Traffic Light */}
              <div className="aam-verdict">
                <span className="aam-zone">{zoneLabel(recommendation.zone)}</span>
                {/* Traffic Light Indicator */}
                <span className="aam-light">
                  <i className={getTrafficLightTone(recommendation.traffic_light)} aria-hidden="true" />
                  {recommendation.traffic_light}
                </span>
              </div>

              {/* Action Indicator */}
              <div className="aam-kv">
                <span className="aam-kv-i">
                  <span className="aam-k">Action:</span>
                  <span className={`aam-v ${getActionTone(recommendation.action_indicator)}`}>
                    {recommendation.action_indicator}
                  </span>
                </span>
                <span className="aam-kv-i">
                  <span className="aam-k">Confidence:</span>
                  <span className={`aam-tag ${getConfidenceTone(recommendation.confidence)}`}>
                    {recommendation.confidence}
                  </span>
                </span>
              </div>

              {/* Market Context Bar */}
              {recommendation.market_context && (
                <div className="aam-tags aam-ctx">
                  {recommendation.market_context.iv_rank != null && (() => {
                    const iv = recommendation.market_context!.iv_rank!;
                    return (
                      <span className="aam-tag">
                        IV Rank <span className="n">{iv.toFixed(0)}%</span>
                      </span>
                    );
                  })()}
                  {recommendation.market_context.cc_score != null && (() => {
                    const score = recommendation.market_context!.cc_score!;
                    return (
                      <span className="aam-tag">
                        CC Score <span className="n">{score}</span>
                      </span>
                    );
                  })()}
                  {recommendation.market_context.trend_signal && (
                    <span className="aam-tag">
                      {recommendation.market_context.trend_signal}
                    </span>
                  )}
                  {recommendation.market_context.days_to_earnings != null && (() => {
                    const days = recommendation.market_context!.days_to_earnings!;
                    const tone = days <= 21 ? 'is-warn' : '';
                    return (
                      <span className={`aam-tag ${tone}`}>
                        Earnings <span className="n">{days}d</span>
                      </span>
                    );
                  })()}
                  {recommendation.market_context.days_to_ex_div != null && (() => {
                    const days = recommendation.market_context!.days_to_ex_div!;
                    const tone = days <= 7 ? 'is-warn' : '';
                    return (
                      <span className={`aam-tag ${tone}`}>
                        Ex-Div <span className="n">{days}d</span>
                      </span>
                    );
                  })()}
                </div>
              )}

              {/* Key Observations */}
              <div className="aam-sec">
                <h4 className="aam-h">Key Observations</h4>
                <ul className="aam-list">
                  {recommendation.key_observations.map((observation, idx) => (
                    <li key={idx}>{observation}</li>
                  ))}
                </ul>
              </div>

              {/* Considerations */}
              <div className="aam-sec">
                <h4 className="aam-h">Considerations</h4>
                <ul className="aam-list is-caveat">
                  {recommendation.considerations.map((consideration, idx) => (
                    <li key={idx}>{consideration}</li>
                  ))}
                </ul>
              </div>

              {/* Analysis */}
              <div className="aam-sec">
                <h4 className="aam-h">Analysis</h4>
                <p className="aam-p">{recommendation.analysis}</p>
              </div>

              {/* Trade Scenario Section */}
              {recommendation.trade_recommendation && recommendation.trade_recommendation.action !== 'hold' && (
                <div className="aam-sec">
                  {/* Educational framing */}
                  <p className="aam-lede">
                    Based on the PME zone above, here is what a {recommendation.trade_recommendation.action} scenario looks like with current market data:
                  </p>
                  {/* Trade Card */}
                  <div className="aam-card">
                    <h4 className="aam-h">
                      {recommendation.trade_recommendation.action === 'roll' ? 'Roll Scenario' : 'Close Scenario'}
                    </h4>

                    {/* Summary */}
                    <p className="aam-summary">{recommendation.trade_recommendation.summary}</p>

                    {/* Trade Legs */}
                    <div className="aam-legs">
                      {recommendation.trade_recommendation.buy_leg && (
                        <div className="aam-leg">
                          <span className="aam-tag">BUY</span>
                          <span className="n">
                            {recommendation.trade_recommendation.buy_leg.action} {recommendation.trade_recommendation.buy_leg.contracts}x {recommendation.trade_recommendation.buy_leg.ticker} {recommendation.trade_recommendation.buy_leg.expiration_display} ${recommendation.trade_recommendation.buy_leg.strike} Call @ ~${recommendation.trade_recommendation.buy_leg.price.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {recommendation.trade_recommendation.sell_leg && (
                        <div className="aam-leg">
                          <span className="aam-tag">SELL</span>
                          <span className="n">
                            {recommendation.trade_recommendation.sell_leg.action} {recommendation.trade_recommendation.sell_leg.contracts}x {recommendation.trade_recommendation.sell_leg.ticker} {recommendation.trade_recommendation.sell_leg.expiration_display} ${recommendation.trade_recommendation.sell_leg.strike} Call @ ~${recommendation.trade_recommendation.sell_leg.price.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Net Credit */}
                    {recommendation.trade_recommendation.net_credit != null && (
                      <div className="aam-net">
                        <span className="aam-k">
                          {recommendation.trade_recommendation.net_credit >= 0 ? 'Net Credit:' : 'Net Debit:'}
                        </span>
                        <span className={`n ${recommendation.trade_recommendation.net_credit >= 0 ? 'is-gain' : 'is-loss'}`}>
                          ${Math.abs(recommendation.trade_recommendation.net_credit).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Risk Flags */}
                    {recommendation.trade_recommendation.risk_flags && recommendation.trade_recommendation.risk_flags.length > 0 && (
                      <div className="aam-tags">
                        {recommendation.trade_recommendation.risk_flags.map((flag, idx) => (
                          <span key={idx} className="aam-tag is-warn">
                            {flag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Reasoning */}
                    {recommendation.trade_recommendation.reasoning && (
                      <p className="aam-reason">{recommendation.trade_recommendation.reasoning}</p>
                    )}
                  </div>

                  {/* Comparison Table (Expandable) */}
                  {recommendation.roll_scenarios && recommendation.roll_scenarios.length > 1 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowComparison(!showComparison)}
                        className="aam-disc"
                        aria-expanded={showComparison}
                      >
                        <span>
                          Compare Alternatives (<span className="n">{recommendation.roll_scenarios.length}</span> candidates)
                        </span>
                        <Chevron open={showComparison} />
                      </button>

                      {showComparison && (
                        <div className="aam-drop">
                          <table className="aam-tbl">
                            <thead>
                              <tr>
                                <th>Contract</th>
                                <th>Delta</th>
                                <th>Price</th>
                                <th>Net Cr.</th>
                                <th>Floor</th>
                                <th>Cushion</th>
                                <th>Ann.Ret</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recommendation.roll_scenarios.map((scenario, idx) => {
                                const isRecommended = recommendation.trade_recommendation?.recommended_scenario_index === scenario.index;
                                return (
                                  <tr key={idx} className={isRecommended ? 'is-pick' : undefined}>
                                    <td>
                                      {isRecommended && '▸ '}{scenario.label}
                                    </td>
                                    <td className="num n">
                                      {scenario.delta != null ? scenario.delta.toFixed(2) : 'N/A'}
                                    </td>
                                    <td className="num n">
                                      ${scenario.last_price != null ? scenario.last_price.toFixed(2) : 'N/A'}
                                    </td>
                                    <td className={`num n is-strong ${scenario.net_roll_credit >= 0 ? 'is-gain' : 'is-loss'}`}>
                                      ${scenario.net_roll_credit.toFixed(2)}
                                    </td>
                                    <td className="num n">
                                      ${scenario.new_safety_floor.toFixed(2)}
                                    </td>
                                    <td className={`num n ${scenario.cushion_pct >= 10 ? '' : 'is-warn'}`}>
                                      {scenario.cushion_pct.toFixed(1)}%
                                    </td>
                                    <td className="num n">
                                      {scenario.annualized_return_pct != null ? `${scenario.annualized_return_pct.toFixed(1)}%` : 'N/A'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Campaign Analysis Section (only shown for multi-leg positions) */}
              {recommendation.campaign_analysis && (
                <div className="aam-sec">
                  {/* Campaign Banner */}
                  <div className="aam-camp-head">
                    <h4 className="aam-h">Campaign Analysis</h4>
                    <span className={`aam-tag ${getCampaignHealthTone(recommendation.campaign_analysis.campaign_health)}`}>
                      <span aria-hidden="true">{getCampaignHealthIcon(recommendation.campaign_analysis.campaign_health)}</span>
                      {recommendation.campaign_analysis.campaign_health}
                    </span>
                  </div>

                  {/* Campaign Metrics Grid */}
                  <div className="aam-grid">
                    {/* Duration */}
                    <div className="aam-cell">
                      <span className="aam-k">Duration</span>
                      <span className="aam-cv n">
                        {recommendation.campaign_analysis.campaign_duration_days} days
                      </span>
                    </div>

                    {/* Rolling Efficiency */}
                    <div className="aam-cell">
                      <span className="aam-k">Rolling Efficiency</span>
                      <span className={`aam-cv n ${recommendation.campaign_analysis.rolling_efficiency >= 60 ? '' : 'is-warn'}`}>
                        {recommendation.campaign_analysis.rolling_efficiency.toFixed(0)}%
                      </span>
                    </div>

                    {/* Net Premium */}
                    <div className="aam-cell">
                      <span className="aam-k">Net Premium Retained</span>
                      <span className={`aam-cv n ${recommendation.campaign_analysis.net_premium_retained >= 0 ? 'is-gain' : 'is-loss'}`}>
                        ${recommendation.campaign_analysis.net_premium_retained.toFixed(2)}
                      </span>
                    </div>

                    {/* Total Collected */}
                    <div className="aam-cell">
                      <span className="aam-k">Total Collected</span>
                      <span className="aam-cv n">
                        ${recommendation.campaign_analysis.total_premium_collected.toFixed(2)}
                      </span>
                    </div>

                    {/* Premium Trend */}
                    <div className="aam-cell">
                      <span className="aam-k">Premium Capture Trend</span>
                      <span className={`aam-cv ${recommendation.campaign_analysis.premium_capture_trend === 'Declining' ? 'is-warn' : ''}`}>
                        {getTrendIcon(recommendation.campaign_analysis.premium_capture_trend)} {recommendation.campaign_analysis.premium_capture_trend}
                      </span>
                    </div>

                    {/* Strike Progression */}
                    <div className="aam-cell">
                      <span className="aam-k">Strike Progression</span>
                      <span className="aam-cv">
                        {getProgressionIcon(recommendation.campaign_analysis.strike_progression)} {recommendation.campaign_analysis.strike_progression}
                      </span>
                    </div>
                  </div>

                  {/* Leg History Timeline (Expandable) */}
                  {recommendation.campaign_analysis.leg_insights && recommendation.campaign_analysis.leg_insights.length > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowLegHistory(!showLegHistory)}
                        className="aam-disc"
                        aria-expanded={showLegHistory}
                      >
                        <span>
                          Leg History (<span className="n">{recommendation.campaign_analysis.leg_insights.length}</span> completed legs)
                        </span>
                        <Chevron open={showLegHistory} />
                      </button>

                      {showLegHistory && (
                        <div className="aam-drop">
                          {recommendation.campaign_analysis.leg_insights.map((leg: AILegInsight, idx: number) => (
                            <div key={idx} className="aam-leg-item">
                              {/* Leg Header */}
                              <div className="aam-leg-head">
                                <span className="aam-leg-id">
                                  <span className="aam-tag">
                                    Leg <span className="n">{leg.leg_number}</span>
                                  </span>
                                  <span className="n is-dim">{leg.duration_days}d</span>
                                </span>
                                <span className={`aam-tag ${getEffectivenessTone(leg.effectiveness)}`}>
                                  {leg.effectiveness}
                                </span>
                              </div>

                              {/* Leg Metrics */}
                              <div className="aam-leg-grid">
                                <div>
                                  <div className="aam-mk">Captured</div>
                                  <div className="aam-mv n">
                                    ${leg.premium_captured.toFixed(2)} ({leg.premium_capture_pct.toFixed(0)}%)
                                  </div>
                                </div>
                                <div>
                                  <div className="aam-mk">Stock Move</div>
                                  <div className={`aam-mv n ${leg.stock_move_pct >= 0 ? 'is-gain' : 'is-loss'}`}>
                                    {leg.stock_move_pct >= 0 ? '+' : ''}{leg.stock_move_pct.toFixed(1)}%
                                  </div>
                                </div>
                                <div>
                                  <div className="aam-mk">Close Reason</div>
                                  <div className="aam-mv">{leg.close_reason}</div>
                                </div>
                              </div>

                              {/* Lesson */}
                              <div className="aam-lesson">{leg.lesson}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Campaign Summary */}
                  {recommendation.campaign_analysis.campaign_summary && (
                    <div className="aam-sec">
                      <h4 className="aam-h">Campaign Summary</h4>
                      <p className="aam-p">{recommendation.campaign_analysis.campaign_summary}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Disclaimer */}
              <p className="aam-fine">
                <strong>Educational tool only.</strong> This analysis and any trade scenarios shown are generated by AI for learning purposes. They are not financial advice, trade recommendations, or solicitations to buy or sell securities. Options trading involves significant risk. You are solely responsible for your own investment decisions. Always do your own research and consult a licensed financial advisor before trading.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="aam-foot">
          <button onClick={onClose} className="aam-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAdvisorModal;
